// Email Template Configuration
import { createTrackingConfig } from '../../components/generic/labels';
import { tableConfig, uiConfig } from '../../config/appConfig';

export const EMAIL_TEMPLATE_TYPE_OPTIONS = [
  { value: 1, label: '1 - Initial Approval Request Email' },
  { value: 2, label: '2 - Reminder Email' },
  { value: 3, label: '3 - Status Update Email (Generic / Fallback)' },
  { value: 4, label: '4 - Status Update Email: Approved' },
  { value: 5, label: '5 - Status Update Email: Rejected' },
  { value: 6, label: '6 - Status Update Email: Cancelled' },
  { value: 7, label: '7 - Status Update Email: Delegated' },
  { value: 21, label: '21 - Ticketing: New Ticket Created' },
  { value: 22, label: '22 - Ticketing: Ticket Updated' },
  { value: 23, label: '23 - Ticketing: Status Changed' },
  { value: 24, label: '24 - Ticketing: New Message Added' },
  { value: 25, label: '25 - Ticketing: Attachment Added' },
  { value: 31, label: '31 - Todo: New Todo Created' },
  { value: 32, label: '32 - Todo: Todo Updated' },
  { value: 41, label: '41 - Reports: Scheduled Report (Generic)' },
  { value: 42, label: '42 - Reports: Compliance' },
  { value: 43, label: '43 - Reports: CAPA' },
  { value: 44, label: '44 - Reports: Risk Assessment' },
  { value: 45, label: '45 - Reports: Non-Conformity' },
  { value: 46, label: '46 - Reports: Quality Metrics' },
  { value: 47, label: '47 - Reports: Supplier' },
  { value: 48, label: '48 - Reports: Complaint' },
  { value: 49, label: '49 - Reports: Document' },
  { value: 50, label: '50 - Reports: Ticketing' },
  { value: 51, label: '51 - Reports: Training' },
];

export const emailTemplateConfig = {
  entityName: 'EmailTemplate',
  
  // Entity information for file attachments and audit logs
  entityTypeId: null, // EmailTemplate doesn't have a specific entity type ID
  
  // Backend API Configuration
  api: {
    baseEndpoint: '/email-templates',
    endpoints: {
      list: '',
      create: '',
      update: (id) => `/${id}`,
      delete: (id) => `/${id}`,
      getById: (id) => `/${id}`,
      getByTemplateId: (templateId) => `/template-id/${templateId}`,
    },
    lookupEndpoints: {},
    swrInvalidation: {
      mainList: '/email-templates',
    }
  },

  // UI Configuration - Using centralized app config
  ui: {
    drawerWidth: uiConfig.defaultDrawerWidth,
    defaultPageSize: tableConfig.defaultPageSize,
    pageSizeOptions: tableConfig.pageSizeOptions,
    defaultTableDensity: tableConfig.defaultDensity,
    requiredFieldStyle: uiConfig.requiredFieldStyle,
    tableVariant: 'default', // 'default' | 'modern' | 'cards' | 'board' | 'timeline'
    splitView: false,
    searchInputMaxWidth: uiConfig.searchInputMaxWidth
  },

  // Service configuration
  service: {
    name: 'emailTemplateService',
    methods: {
      getAll: 'getAllEmailTemplates',
      getById: 'getEmailTemplateById',
      create: 'createEmailTemplate',
      update: 'updateEmailTemplate',
      delete: 'deleteEmailTemplate',
      validate: 'validateData'
    }
  },

  // Display Configuration
  displayName: 'Email Templates',
  description: 'Manage email templates for approval request notifications',
  icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',

  // Features configuration
  features: {
    fileAttachments: false,
    masterEntityTypeId: null,
  },

  // Custom handlers
  customHandlers: {
    // Opens HTML preview modal using the templateBody field
    // The handler is wrapped in GenericFormsPage to set htmlPreviewRecord state
    onViewHtml: (record) => {
      // This will be wrapped by GenericFormsPage to open a modal
      // Just return the record - the wrapper will handle the modal
      return record;
    },
  },

  // Fields to remove from forms before submission (relations/computed)
  fieldsToRemove: [
    'createdBy', 'updatedBy', 'createdAt', 'updatedAt', 'createdById', 'updatedById', 'fileAttachments'
  ],

  // Transform data before sending to API
  transformData: (data, formMode = 'add') => {
    const transformed = { ...data };
    
    // Ensure templateId is always a number (required by shared validator).
    if (transformed.templateId !== undefined) {
      const templateIdNum = parseInt(transformed.templateId, 10);
      if (!isNaN(templateIdNum)) {
        transformed.templateId = templateIdNum;
      } else {
        console.error('[EmailTemplate] Invalid templateId:', transformed.templateId);
        delete transformed.templateId;
      }
    }
    
    // Ensure isActive is a boolean
    if (transformed.isActive !== undefined) {
      transformed.isActive = transformed.isActive === true || transformed.isActive === 'true' || transformed.isActive === 1;
    }
    
    // Remove fields that shouldn't be sent
    const fieldsToRemove = ['id', 'createdBy', 'updatedBy', 'createdAt', 'updatedAt', 'fileAttachments'];
    fieldsToRemove.forEach(field => {
      delete transformed[field];
    });
    
    console.log('[EmailTemplate] Transformed data for API:', transformed);
    return transformed;
  },

  // UNIFIED FIELD CONFIGURATION
  fields: [
    {
      name: 'id',
      label: 'ID',
      type: 'hidden',
      table: false,
      filterable: false
    },
    {
      name: 'templateId',
      label: 'Template Type',
      type: 'select',
      required: true,
      options: EMAIL_TEMPLATE_TYPE_OPTIONS,
      table: {
        header: 'Template Type',
        width: '150px',
        sortable: true,
        cellRenderer: (value) => {
          const labels = {
            1: 'Initial',
            2: 'Reminder',
            3: 'Status Update',
            4: 'Approved',
            5: 'Rejected',
            6: 'Cancelled',
            7: 'Delegated',
            21: 'Ticket Created',
            22: 'Ticket Updated',
            23: 'Ticket Status',
            24: 'Ticket Message',
            25: 'Ticket Attachment',
            31: 'Todo Created',
            32: 'Todo Updated',
            41: 'Report Generic',
            42: 'Report Compliance',
            43: 'Report CAPA',
            44: 'Report Risk',
            45: 'Report NCR',
            46: 'Report Quality',
            47: 'Report Supplier',
            48: 'Report Complaint',
            49: 'Report Document',
            50: 'Report Ticketing',
            51: 'Report Training',
          };
          return labels[value] || value;
        }
      },
      filterable: true,
      validation: {
        required: true,
      },
      helpText: 'Select template type (Approval: 1-7, Ticketing: 21-25, Todo: 31-32, Reports: 41-51)'
    },
    {
      name: 'templateSubject',
      label: 'Email Subject',
      type: 'text',
      required: true,
      placeholder: 'e.g., Approval Request: {approvalRequestCode}',
      table: {
        header: 'Subject',
        width: '250px',
        sortable: true,
        cellRenderer: 'truncate',
        truncateLength: 50
      },
      filterable: true,
      validation: {
        required: true,
        maxLength: 255
      },
      helpText: 'Email subject line. Use {variableName} for dynamic content (e.g., {approvalRequestCode}, {approverName})'
    },
    {
      name: 'templateBody',
      label: 'Email Body (HTML)',
      type: 'textarea',
      required: true,
      placeholder: 'Enter HTML email template with variables...',
      rows: 15,
      table: false,
      filterable: false,
      validation: {
        required: true,
        maxLength: 10000
      },
      helpText: 'HTML body with {variables}. Approval examples: {approvalRequestCode}, {approvalRequestTitle}, {approvalLink}. Ticketing examples: {ticketId}, {ticketStatusLabel}, {ticketDepartment}, {ticketCategory}, {actionLabel}, {actionDetails}, {actorName}, {messagePreview}, {ticketUrl}, {companyName}, {currentDate}. Todo examples: {todoId}, {todoSubject}, {todoDescription}, {todoTaskList}, {todoTaskListHtml}, {todoStatus}, {todoCompleted}, {todoDueDate}, {todoCreatedAt}, {todoUpdatedAt}, {todoOwnerName}, {todoOwnerEmail}, {todoAssigneeName}, {todoAssigneeEmail}, {todoFileAttachmentCount}, {todoFileAttachmentsHtml}, {todoUrl}. Report Schedule examples: {scheduleName}, {scheduleDescription}, {reportType}, {reportTitle}, {reportPeriodLabel}, {startDate}, {endDate}, {generatedAt}, {reportSummaryHtml}, {attachmentsList}, {reportLinkUrl}, plus per-KPI {kpi_<name>} (e.g. {kpi_total_capas}).'
    },
    {
      name: 'isActive',
      label: 'Active',
      type: 'switch',
      required: false,
      table: {
        header: 'Active',
        width: '80px',
        sortable: true,
        cellRenderer: 'boolean'
      },
      filterable: true,
      validation: {},
      helpText: 'Only active templates will be used for sending emails'
    },
    {
      name: 'createdBy',
      label: 'Created By',
      type: 'text',
      required: false,
      table: false,
      filterable: false,
      form: false
    },
    {
      name: 'updatedBy',
      label: 'Updated By',
      type: 'text',
      required: false,
      table: false,
      filterable: false,
      form: false
    },
    {
      name: 'createdAt',
      label: 'Created At',
      type: 'datetime',
      required: false,
      table: false,
      filterable: false,
      form: false
    },
    {
      name: 'updatedAt',
      label: 'Updated At',
      type: 'datetime',
      required: false,
      table: false,
      filterable: false,
      form: false
    }
  ],

  // COMPUTED CONFIGURATIONS - Generated from unified fields (no maintenance needed!)
  get tableColumns() {
    const columns = this.fields
      .filter(field => field.table !== false && field.table)
      .map(field => {
        const column = {
          key: field.table.displayField || field.name,
          header: field.table.header || field.label,
          width: field.table.width || 'auto',
          sortable: field.table.sortable !== false,
          displayField: field.table.displayField || field.name,
          originalField: field.name
        };
        
        // Handle cellRenderer: if it's a function, use it as 'render', otherwise use as string
        if (field.table.cellRenderer) {
          if (typeof field.table.cellRenderer === 'function') {
            column.render = field.table.cellRenderer;
          } else {
            column.cellRenderer = field.table.cellRenderer;
          }
        } else {
          column.cellRenderer = 'default';
        }
        
        return column;
      });
    return columns;
  },

  exportFileName: 'qms_email_templates',

  // Backend field mappings (nested display fields if any)
  fieldMappings: {
    // none required for email template fields
  },

  get filterableFields() {
    return this.fields
      .filter(field => field.filterable === true)
      .map(field => field.name);
  },

  get validationRules() {
    const rules = {};
    this.fields.forEach(field => {
      if (field.validation) {
        rules[field.name] = field.validation;
      }
    });
    return rules;
  },

  // Custom Sort Orders
  customSortOrders: {
    templateId: 'asc', // Sort by template ID by default
    isActive: [true, false] // Active templates first
  },

  // Filter Groups Configuration - Groups filters into tabs for better organization
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: [
        'templateId', 'isActive'
      ]
    },
    {
      name: 'content',
      label: 'Content',
      icon: '📧',
      fields: [
        'templateSubject'
      ]
    }
  ],

  // Form Layout Configuration - Single source of truth
  formSections: [
    {
      title: 'Template Information',
      icon: '📧',
      layout: 'grid',
      gridCols: 'md:grid-cols-12',
      fields: ['templateId', 'templateSubject', 'templateBody'],
      fieldSpans: {
        'templateId': 'md:col-span-12',
        'templateSubject': 'md:col-span-12',
        'templateBody': 'md:col-span-12'
      }
    },
    {
      title: 'Status',
      icon: '✅',
      layout: 'grid',
      gridCols: 'md:grid-cols-2',
      fields: ['isActive']
    }
  ],

  // Tracking section configuration (auto-generated)
  trackingSection: createTrackingConfig('EmailTemplate')
};

export default emailTemplateConfig;

